import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Card, CardContent, Skeleton } from "@mui/material";
import VipHeaderSection from "./VipHeaderSection";
import { useDispatch, useSelector } from "react-redux";
import { getVipActiveConfigurationsRequest } from "../../redux/Profile/profileSlice";
import GoldImage from "../../bars-gold.jpg";
import { useAppTheme } from "../../theme/useAppTheme";

const MESSAGES = {
  vipMember: "VIP Member",
  noConfiguration:
    "Great things take time. We are building something special for you! A golden feature is on it's way.✨",
  comingSoon: "COMING SOON...",
  completeTask: "Complete the below task to be a",
  yourJourney: "Your VIP Journey",
  benefitDetails: "Benefit Details",
  criteriaDetails: "Criteria Details",
  vipBenefits: "VIP Benefits",
  howToBecome: "How to Become VIP",
  noCriteria: "No criteria available",
  noBenefits: "No benefits available",
  noCriteriaDetails: "No criteria details available",
};

const EmptyState = ({ themeColors }) => (
  <Box
    sx={{
      minHeight: "60vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      px: 3,
      pb: 5,
    }}
  >
    <Typography
      sx={{ color: themeColors.primary, fontWeight: 600, fontSize: 16 }}
    >
      {MESSAGES.noConfiguration}
    </Typography>
    <Typography
      sx={{ color: themeColors.primary, fontWeight: 700, fontSize: 22, mt: 1.5 }}
    >
      {MESSAGES.comingSoon}
    </Typography>
  </Box>
);

const EmptyMessage = ({ children, themeColors }) => (
  <Typography sx={{ textAlign: "center", py: 2.5, color: themeColors.text }}>
    {children}
  </Typography>
);

const ListCard = ({ title, note, onClick, themeColors }) => (
  <Card
    sx={{
      mb: 1.5,
      cursor: onClick ? "pointer" : "default",
      background: themeColors.mediumContainer,
      color: themeColors.text,
      border: `1px solid ${themeColors.border}`,
      borderRadius: "12px",
    }}
    onClick={onClick}
  >
    <CardContent>
      <Typography fontWeight="bold" sx={{ fontSize: 14 }}>
        {title}
      </Typography>
      {note ? (
        <Typography sx={{ fontSize: 12, mt: 0.5, lineHeight: 1.5 }}>
          {note}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const criteriaMessage = (config) => {
  if (config.customMessage) return config.customMessage;
  const amountType = (config.amountType || "").toLowerCase();
  return `Buy ${config.quantity} ${amountType} of ${config.metalName} to become a VIP.`;
};

const VipBenefitDetails = ({ benefit, themeColors }) => {
  const enabledConfigs = (benefit?.config || []).filter(
  (item) => item.enabled && item.benefitMetalName && Number(item.quantity) > 0,
);

  if (!benefit || !enabledConfigs.length) {
    return (
      <EmptyMessage themeColors={themeColors}>{MESSAGES.noBenefits}</EmptyMessage>
    );
  }

  return (
    <>
      {enabledConfigs.map((item, index) => (
        <Card
          key={index}
          sx={{
            mb: 2,
            overflow: "hidden",
            position: "relative",
            height: { xs: 130, md: 90 },
            borderRadius: "12px",
          }}
        >
          <Box
            component="img"
            src={GoldImage}
            alt=""
            sx={{ height: "100%", width: "100%", objectFit: "cover" }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to bottom, transparent, rgba(0,0,0,0.55))",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 12,
              color: "#fff",
            }}
          >
            <Typography fontWeight="bold">{benefit.displayName}</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 14 }}>
              {item.customMessage || benefit.note}
            </Typography>
          </Box>
        </Card>
      ))}
    </>
  );
};

const VipCriteriaDetails = ({ criteria, themeColors }) => {
  if (!criteria) {
    return (
      <EmptyMessage themeColors={themeColors}>
        {MESSAGES.noCriteriaDetails}
      </EmptyMessage>
    );
  }

  if (criteria.criteriaType === "sip") {
    return (
      <>
        <ListCard title={criteria.displayName} themeColors={themeColors} />
        <ListCard
          title={criteria.customMessage || ""}
          themeColors={themeColors}
        />
      </>
    );
  }

  const enabledConfigs = (criteria.config || []).filter((item) => item.enabled);

  if (!enabledConfigs.length) {
    return (
      <ListCard
        title={criteria.displayName}
        note={criteria.note}
        themeColors={themeColors}
      />
    );
  }

  return (
    <>
      <ListCard title={criteria.displayName} themeColors={themeColors} />
      {enabledConfigs.map((config, index) => (
        <ListCard
          key={index}
          title={criteriaMessage(config)}
          themeColors={themeColors}
        />
      ))}
    </>
  );
};

const VipMembershipPage = () => {
  const navigate = useNavigate();
  const { vipActiveConfigurations, loading, customer } = useSelector(
    (state) => state.profile || {},
  );
  const dispatch = useDispatch();
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const themeColors = useAppTheme();

  const isVip = customer?.customer_type === "vip";

  useEffect(() => {
    dispatch(getVipActiveConfigurationsRequest());
  }, [dispatch]);

  const handleBack = () => {
    if (showDetailView) {
      setShowDetailView(false);
      setSelectedBenefit(null);
      setSelectedCriteria(null);
      return;
    }
    navigate(-1);
  };

  if (loading) {
    return <Skeleton variant="rectangular" height={300} sx={{ m: 2 }} />;
  }

  if (!vipActiveConfigurations?.value) {
    return (
      <Box sx={{ background: themeColors.scaffoldGradientColors, minHeight: "100vh" }}>
        <EmptyState themeColors={themeColors} />
      </Box>
    );
  }

  const criteriaList = (vipActiveConfigurations.value.config || []).filter(
    (item) => item.enabled,
  );
  const benefitsList = (vipActiveConfigurations.value.benefits || []).filter(
    (item) => item.enabled,
  );

  const sectionTitle = showDetailView
    ? isVip
      ? MESSAGES.benefitDetails
      : MESSAGES.criteriaDetails
    : isVip
      ? MESSAGES.vipBenefits
      : MESSAGES.howToBecome;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: 2,
        minHeight: "100vh",
        background: themeColors.scaffoldGradientColors,
        color: themeColors.scaffoldText,
      }}
    >
      <Typography
        sx={{ fontWeight: 700, mb: 2, color: themeColors.scaffoldText }}
      >
        {MESSAGES.vipMember}
      </Typography>

      <VipHeaderSection
        vipMember={isVip}
        progress={isVip ? 100 : 45.6}
        progressText={isVip ? MESSAGES.yourJourney : MESSAGES.completeTask}
        onBack={handleBack}
        themeColors={themeColors}
      />

      <Box mt={2}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            mb: 1.25,
            color: themeColors.scaffoldText,
          }}
        >
          {sectionTitle}
        </Typography>

        {!showDetailView && isVip && (
          benefitsList.length ? (
            benefitsList.map((item, index) => (
              <ListCard
                key={index}
                title={item.displayName}
                note={item.note}
                themeColors={themeColors}
                onClick={() => {
                  setSelectedBenefit(item);
                  setShowDetailView(true);
                }}
              />
            ))
          ) : (
            <EmptyMessage themeColors={themeColors}>
              {MESSAGES.noBenefits}
            </EmptyMessage>
          )
        )}

        {!showDetailView && !isVip && (
          criteriaList.length ? (
            criteriaList.map((item, index) => (
              <ListCard
                key={index}
                title={item.displayName}
                note={item.note}
                themeColors={themeColors}
                onClick={() => {
                  setSelectedCriteria(item);
                  setShowDetailView(true);
                }}
              />
            ))
          ) : (
            <EmptyMessage themeColors={themeColors}>
              {MESSAGES.noCriteria}
            </EmptyMessage>
          )
        )}

        {showDetailView && isVip && (
          <VipBenefitDetails benefit={selectedBenefit} themeColors={themeColors} />
        )}

        {showDetailView && !isVip && (
          <VipCriteriaDetails
            criteria={selectedCriteria}
            themeColors={themeColors}
          />
        )}
      </Box>
    </Box>
  );
};

export default VipMembershipPage;
